/** Build-time daily order, validator, and freeze/rebuild. */

import { CARDS_PER_DAY } from './config.js';
import {
  BuildFailure,
  frozenThroughDay,
  loadPool,
  requireWindow,
  validatorDayRange,
  yearGap,
  type HistoryEvent,
} from './pool.js';

export { CARDS_PER_DAY };
const WEEK_ONE_LAST_DAY = 7;
const WEEK_ONE_GAP = 25;
const NORMAL_GAP = 10;

export type ScheduledDay = { day: number; cards: HistoryEvent[] };
export type Schedule = { launchDate: string; window: number; days: ScheduledDay[] };

function byId(a: HistoryEvent, b: HistoryEvent): number {
  return a.id.localeCompare(b.id);
}

function minGap(day: number): number {
  return day <= WEEK_ONE_LAST_DAY ? WEEK_ONE_GAP : NORMAL_GAP;
}

function lastUseOn(days: ScheduledDay[]): Map<string, number> {
  const uses = new Map<string, number>();
  for (const scheduled of days) {
    for (const card of scheduled.cards) uses.set(card.id, scheduled.day);
  }
  return uses;
}

function isFree(id: string, day: number, uses: Map<string, number>, window: number): boolean {
  const last = uses.get(id);
  return last === undefined || last < day - window;
}

function fitsGaps(picked: HistoryEvent[], candidate: HistoryEvent, gap: number): boolean {
  return picked.every((card) => yearGap(card.year, candidate.year) >= gap);
}

/** Prefer a familiar headliner seed, then fill with familiars; store sorted by id. */
function pickRound(
  pool: HistoryEvent[],
  day: number,
  uses: Map<string, number>,
  window: number,
): HistoryEvent[] {
  const gap = minGap(day);
  const available = pool.filter((event) => isFree(event.id, day, uses, window));
  const familiars = available.filter((event) => event.familiar).sort(byId);
  const seeds = [
    ...familiars.filter((event) => event.headliner),
    ...familiars.filter((event) => !event.headliner),
  ];

  for (const seed of seeds) {
    const picked = [seed];
    for (const event of familiars) {
      if (picked.some((card) => card.id === event.id)) continue;
      if (!fitsGaps(picked, event, gap)) continue;
      picked.push(event);
      if (picked.length === CARDS_PER_DAY) {
        return [...picked].sort(byId);
      }
    }
  }
  throw new BuildFailure('unfilled_day', `Could not fill day ${day}`, { day });
}

function rolesOk(day: ScheduledDay): boolean {
  return day.cards.length === CARDS_PER_DAY && day.cards.every((card) => card.familiar);
}

function gapsOk(day: ScheduledDay): boolean {
  const gap = minGap(day.day);
  for (let i = 0; i < day.cards.length; i += 1) {
    for (let j = i + 1; j < day.cards.length; j += 1) {
      const a = day.cards[i];
      const b = day.cards[j];
      if (!a || !b || yearGap(a.year, b.year) < gap) return false;
    }
  }
  return true;
}

function uniqueInRound(day: ScheduledDay): boolean {
  return new Set(day.cards.map((card) => card.id)).size === day.cards.length;
}

function repeatsOk(day: ScheduledDay, prior: ScheduledDay[], window: number): boolean {
  const uses = lastUseOn(prior);
  return day.cards.every((card) => isFree(card.id, day.day, uses, window));
}

/** Validate future days against gap, role, uniqueness, and W. Frozen days are identity-only. */
export function validateSchedule(schedule: Schedule, frozenThrough: number): void {
  const ordered = [...schedule.days].sort((a, b) => a.day - b.day);
  for (const [index, day] of ordered.entries()) {
    if (day.cards.length !== CARDS_PER_DAY) {
      throw new BuildFailure(
        'unfilled_day',
        `Day ${day.day} does not have ${CARDS_PER_DAY} cards`,
        { day: day.day },
      );
    }
    if (day.day <= frozenThrough) continue;
    const prior = ordered.slice(0, index);
    if (!rolesOk(day) || !gapsOk(day) || !uniqueInRound(day) || !repeatsOk(day, prior, schedule.window)) {
      throw new BuildFailure('unfilled_day', `Day ${day.day} fails schedule rules`, { day: day.day });
    }
  }
}

export function frozenSnapshot(schedule: Schedule, throughDay: number): string {
  return JSON.stringify(
    [...schedule.days].filter((day) => day.day <= throughDay).sort((a, b) => a.day - b.day),
  );
}

export function buildSchedule(opts: {
  rows: unknown[];
  launchDate: string;
  now: Date;
  previous?: Schedule | null;
}): Schedule {
  const loaded = loadPool(opts.rows);
  const window = requireWindow(loaded.admitted.length);
  const freezeAt = opts.previous ? frozenThroughDay(opts.now, opts.launchDate) : 0;
  const range = validatorDayRange(opts.now, opts.launchDate);
  const days: ScheduledDay[] = [];

  if (freezeAt > 0 && opts.previous) {
    const frozen = opts.previous.days.filter((day) => day.day >= 1 && day.day <= freezeAt);
    if (frozen.length !== freezeAt) {
      throw new BuildFailure('unfilled_day', `Previous schedule missing frozen days 1–${freezeAt}`, {
        day: freezeAt,
      });
    }
    days.push(...frozen.sort((a, b) => a.day - b.day));
  }

  const uses = lastUseOn(days);
  for (let day = freezeAt + 1; day <= range.to; day += 1) {
    const cards = pickRound(loaded.admitted, day, uses, window);
    days.push({ day, cards });
    for (const card of cards) uses.set(card.id, day);
  }

  const schedule = { launchDate: opts.launchDate, window, days };
  validateSchedule(schedule, freezeAt);
  return schedule;
}

export function dayCards(schedule: Schedule, dayNumber: number): ScheduledDay | undefined {
  return schedule.days.find((day) => day.day === dayNumber);
}
